import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { notes as notesApi, type EntityNote } from "../../api/endpoints";
import { AddToast } from "../../components/feedback/Toast";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { SectionLabel } from "../../components/ui/SectionLabel";
import { F, T } from "../../theme/tokens";

// Internal notes on a talent or organisation. They were saved but there was nowhere
// to read them back; this is that place, with the form to add one.

export const NOTES_FIELD_ID = "internal-note-field";

/** Scrolls to the notes and puts the cursor in the note box (the "Add internal note" buttons). */
export function focusNotes() {
  const el = document.getElementById(NOTES_FIELD_ID) as HTMLTextAreaElement | null;
  el?.scrollIntoView({ behavior: "smooth", block: "center" });
  el?.focus({ preventScroll: true });
}

export function NotesCard({ entityType, entityId, addToast }: { entityType: "Talent" | "Organisation"; entityId: string; addToast: AddToast }) {
  const [items, setItems] = useState<EntityNote[]>([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    notesApi.list(entityType, entityId).then(setItems).catch(() => setItems([]));
  }, [entityType, entityId]);

  useEffect(load, [load]);

  function add() {
    const note = draft.trim();
    if (!note) return;
    setSaving(true);
    notesApi.create({ entity_type: entityType, entity_id: entityId, note })
      .then(() => { setDraft(""); load(); addToast("Note saved", "success"); })
      .catch((e) => addToast(e instanceof Error ? e.message : "Failed to save note", "error"))
      .finally(() => setSaving(false));
  }

  function remove(id: string) {
    notesApi.remove(id)
      .then(() => { load(); addToast("Note deleted", "info"); })
      .catch(() => addToast("Failed to delete note", "error"));
  }

  return (
    <Card style={{ padding: "20px" }}>
      <SectionLabel>Internal notes</SectionLabel>
      <p style={{ fontSize: "11px", color: T.muted, fontFamily: F, margin: "-6px 0 12px" }}>Only admins see these. They are never shown to the user.</p>
      <textarea
        id={NOTES_FIELD_ID}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={3}
        placeholder="Write a note…"
        style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: "8px", padding: "9px 12px", fontSize: "13px", fontFamily: F, outline: "none", color: T.text, background: T.surface, boxSizing: "border-box", resize: "vertical", marginBottom: "8px" }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: items.length ? "14px" : 0 }}>
        <Btn variant="primary" size="xs" onClick={add} disabled={saving || !draft.trim()}>{saving ? "Saving…" : "Add note"}</Btn>
      </div>
      {items.length === 0 ? (
        <p style={{ fontSize: "12px", color: T.muted, fontFamily: F, margin: "8px 0 0" }}>No notes yet.</p>
      ) : (
        items.map((n, i) => (
          <div key={n.id} style={{ padding: "10px 0", borderTop: i === 0 ? `1px solid ${T.border}` : "none", borderBottom: i < items.length - 1 ? `1px solid ${T.border}` : "none", display: "flex", gap: "10px", alignItems: "flex-start" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: "12px", color: T.text, fontFamily: F, lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{n.note}</p>
              <p style={{ fontSize: "10px", color: T.muted, fontFamily: F, margin: "4px 0 0" }}>{n.created_by ?? "Admin"} · {new Date(n.created_at).toLocaleString()}</p>
            </div>
            <button onClick={() => remove(n.id)} aria-label="Delete note" style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, padding: "2px" }}><Trash2 size={13} /></button>
          </div>
        ))
      )}
    </Card>
  );
}
